/**
 * Builds RecalcConfig for NC, Direct, and Indirect templates.
 * Each config merges shared formulas with template-specific formulas and provides getBlendedWhlsFee.
 * Shared formulas are the single source for recalc; root bidAnalysisFormulas re-exports for other consumers.
 */

import * as SharedFormulas from './shared/formulas/bidAnalysisFormulas.shared';
import * as NcFormulas from './templates/NC/ncFormulas';
import * as DirectFormulas from './templates/MASDirect/masDirectFormulas';
import * as IndirectFormulas from './templates/MASIndirect/masIndirectFormulas';
import { calculateBlendedWhlsFee } from './blendedWhlsFeeCalculator';
import { calculateNCBlendedWhlsFee, whlsFeeCalculationNC } from './ncBlendedWhlsFeeCalculator';
import type { RecalcConfig, BlendedWhlsFeeContext } from './recalcCore';

function buildNCGetBlendedWhlsFee(): (ctx: BlendedWhlsFeeContext) => number {
  return (ctx: BlendedWhlsFeeContext): number => {
    if (!ctx.isNC) return 0;

    // Priority for date selection:
    // 1. updatedData.targetLaunchDate — set by useBidHandlers to the year-specific date for option years
    // 2. newData.targetLaunchDate — fallback from spread of updatedData
    // 3. For option years without an explicit date: calculate from templateData base + yearIndex
    // 4. usagePeriodStartDate — final fallback
    let dateToUse: string | Date | null | undefined =
      ctx.updatedData.targetLaunchDate || ctx.newData.targetLaunchDate;

    const usagePeriodStartDate = ctx.newData.usagePeriodStartDate || ctx.updatedData.usagePeriodStartDate || ctx.templateData?.usagePeriodStartDate;

    // Only fall back to calculating from templateData base date when no explicit date is available
    if (!dateToUse && ctx.yearIndex != null && ctx.yearIndex > 0) {
      const baseDate = ctx.templateData?.targetLaunchDate;
      if (baseDate) {
        try {
          const base = typeof baseDate === 'string' ? new Date(baseDate) : baseDate;
          if (base instanceof Date && !isNaN(base.getTime())) {
            const yearDate = new Date(base);
            yearDate.setFullYear(base.getFullYear() + ctx.yearIndex);
            dateToUse = yearDate;
          }
        } catch (_e) {
          /* fallback */
        }
      }
    }

    // Also try templateData?.targetLaunchDate as a base-year date when no year-specific date found
    if (!dateToUse) {
      dateToUse = ctx.templateData?.targetLaunchDate;
    }

    if (dateToUse) {
      return whlsFeeCalculationNC(
        dateToUse,
        ctx.suggestedBidPrice,
        ctx.vaAnnualNcEst,
        ctx.dodAnnualNcEst,
        ctx.ihsAnnualNcEst,
        ctx.bopAnnualNcEst
      );
    }
    if (usagePeriodStartDate) {
      return calculateNCBlendedWhlsFee(
        usagePeriodStartDate,
        ctx.suggestedBidPrice,
        ctx.vaAnnualNcEst,
        ctx.dodAnnualNcEst,
        ctx.ihsAnnualNcEst,
        ctx.bopAnnualNcEst
      );
    }
    return 0;
  };
}

function buildDirectGetBlendedWhlsFee(): (ctx: BlendedWhlsFeeContext) => number {
  return (ctx: BlendedWhlsFeeContext): number => {
    if (!ctx.isDirect) return 0;
    const targetLaunchDate =
      ctx.newData.targetLaunchDate ||
      ctx.updatedData.targetLaunchDate ||
      ctx.templateData?.targetLaunchDate;
    const vaTms = parseFloat(ctx.newData.vaTmsPercent) || parseFloat(ctx.updatedData.vaTmsPercent) || 0;
    const dodTms = parseFloat(ctx.newData.dodTmsPercent) || parseFloat(ctx.updatedData.dodTmsPercent) || 0;
    const vaUsageForWhls =
      parseFloat(ctx.newData.vaAnnualUsage) || parseFloat(ctx.updatedData.vaAnnualUsage) || 0;
    const dodUsageForWhls =
      parseFloat(ctx.newData.dodAnnualUsage) || parseFloat(ctx.updatedData.dodAnnualUsage) || 0;
    if (targetLaunchDate && ctx.suggestedBidPrice > 0) {
      return calculateBlendedWhlsFee(
        targetLaunchDate,
        ctx.suggestedBidPrice,
        vaUsageForWhls,
        dodUsageForWhls,
        vaTms,
        dodTms
      );
    }
    return 0;
  };
}

export function getNCRecalcConfig(fieldDependencies: Record<string, string[]>): RecalcConfig {
  const formulas = { ...SharedFormulas, ...NcFormulas } as Record<string, (...args: any[]) => any>;
  return {
    formulas,
    fieldDependencies,
    getBlendedWhlsFee: buildNCGetBlendedWhlsFee(),
  };
}

export function getDirectRecalcConfig(fieldDependencies: Record<string, string[]>): RecalcConfig {
  const formulas = { ...SharedFormulas, ...DirectFormulas } as Record<string, (...args: any[]) => any>;
  return {
    formulas,
    fieldDependencies,
    getBlendedWhlsFee: buildDirectGetBlendedWhlsFee(),
  };
}

export function getIndirectRecalcConfig(fieldDependencies: Record<string, string[]>): RecalcConfig {
  const formulas = { ...SharedFormulas, ...IndirectFormulas } as Record<string, (...args: any[]) => any>;
  return {
    formulas,
    fieldDependencies,
    getBlendedWhlsFee: () => 0,
  };
}
